import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { useDueItems } from '../hooks/useDueItems'
import { useGames } from '../hooks/useGames'

/**
 * Ce qui est dû aujourd'hui pour la répétition espacée (SPEC §10, §12,
 * lot S) : une bannière, pas un nouvel écran — la page des jeux suffit.
 *
 * L'accès direct proposé renvoie vers la première mécanique jouable du
 * catalogue (`useGames`, déjà chargé par `GameCatalog` sur cette même page —
 * même clé de cache `react-query`, donc aucun appel réseau supplémentaire) :
 * `ItemSelector` priorise déjà les items dus dans toute partie `solo`
 * démarrée (`StartRunHandler` → `selectPersonalizedCandidates`), quelle que
 * soit la mécanique choisie — y jouer révise donc en priorité ce qui est dû,
 * sans qu'il faille inventer une mécanique dédiée.
 *
 * Rien à afficher tant qu'aucun item n'est dû (`total === 0`) : ni
 * l'apprenant qui n'a jamais joué (aucune ligne `item_reviews`), ni celui qui
 * est à jour, n'ont besoin qu'on le leur signale — SPEC §10, « le comptage
 * doit être juste quand rien n'est dû ». Affiche `total`, jamais `count` :
 * `count` n'est que la taille de la page servie par le serveur (bornée par
 * `limit`), `total` le nombre réel d'items dus — voir le docblock de
 * `dueItemsSchema`.
 *
 * Rien pendant le chargement non plus, plutôt qu'un `Skeleton` : c'est une
 * surface facultative, pas le contenu principal de la page, et la majorité
 * des apprenants (compte à zéro) verrait sinon un placeholder apparaître puis
 * disparaître, décalant `DailyCard`/`GameCatalog` en dessous pour rien.
 */
export function DueBanner() {
  const { t } = useTranslation()
  const { data: due, isLoading, isError } = useDueItems()
  const { data: games } = useGames()

  if (isLoading) {
    return null
  }

  if (isError) {
    return <Alert variant="error">{t('practice.due.load_error')}</Alert>
  }

  if (!due || due.total === 0) {
    return null
  }

  const target = games && games.length > 0 ? games[0].id : null

  return (
    <section
      aria-labelledby="practice-due-title"
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-secondary/10 px-4 py-3 md:px-5"
    >
      <p id="practice-due-title" className="text-sm font-medium text-on-surface">
        {t('practice.due.count', { count: due.total })}
      </p>

      {target && (
        <Link
          to={`/jeux/${target}`}
          className="inline-flex min-h-[44px] items-center rounded-full bg-secondary px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          {t('practice.due.cta')}
        </Link>
      )}
    </section>
  )
}
